import { createContext, useContext, useEffect, useState, useSyncExternalStore } from "react";
import { toast } from "sonner";
import { Feather } from "lucide-react";
import { api } from "@/lib/api";
import { loadDict, onDictChange, setCustom, dictReady } from "@/lib/poetry/dict";
import { refreshPoetry } from "@/components/poetry/PoetryMarks";
import { RhymesTab } from "@/components/poetry/RhymesTab";
import { GuideTab } from "@/components/poetry/GuideTab";
import { WordsTab } from "@/components/poetry/WordsTab";

export const PoetryContext = createContext(null);
const PRON_KEY = "lf-pronunciations";
let ver = 0;
onDictChange(() => { ver += 1; });

export const useDictVersion = () => useSyncExternalStore(onDictChange, () => ver);

export const loadProns = async () => {
  try { setCustom(JSON.parse(localStorage.getItem(PRON_KEY)) || []); } catch { /* none cached */ }
  try { const { data } = await api.get("/me/pronunciations"); localStorage.setItem(PRON_KEY, JSON.stringify(data.words)); setCustom(data.words); } catch { /* offline */ }
};

export const saveProns = async (list) => {
  setCustom(list);
  localStorage.setItem(PRON_KEY, JSON.stringify(list));
  await api.put("/me/pronunciations", { words: list });
};

export const usePoetryDict = (enabled, editor) => {
  useEffect(() => {
    if (!enabled) return undefined;
    loadDict().catch(() => toast.error("Couldn't load the word list. Counts are estimated until you're back online."));
    loadProns();
    return onDictChange(() => refreshPoetry(editor));
  }, [enabled, editor]);
};

export const useEditorTick = (editor) => {
  const [, setT] = useState(0);
  useEffect(() => {
    if (!editor) return undefined;
    let raf = 0;
    const on = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(() => setT((t) => t + 1)); };
    editor.on("transaction", on);
    return () => { cancelAnimationFrame(raf); editor.off("transaction", on); };
  }, [editor]);
};

const MODES = [["prose", "Prose"], ["poem", "Poem"], ["song", "Song"], ["screenplay", "Screen"], ["stage", "Stage"]];
export const ModeSwitch = ({ mode, setMode, disabled }) => (
  <div className="flex overflow-hidden rounded border border-[#2D3442]" title="Poem and song chapters show syllables and rhymes; Screen and Stage format the chapter as a screenplay or stage play" data-testid="chapter-mode-switch">
    {MODES.map(([k, l]) => <button key={k} disabled={disabled} onClick={() => k !== mode && setMode(k)} className={`px-2.5 py-1 text-xs ${mode === k ? "bg-[#D97706] text-[#0B0D10]" : "hover:bg-[#1A202C]"}`} data-testid={`chapter-mode-${k}`}>{l}</button>)}
  </div>
);

const TABS = [["rhymes", "Rhymes"], ["guide", "Guide"], ["words", "My words"]];

export const PoetryWidget = ({ onInsert }) => {
  const ctx = useContext(PoetryContext);
  const [tab, setTab] = useState("rhymes");
  useDictVersion();
  useEditorTick(ctx?.editor);
  if (!ctx) return <p className="py-6 text-center text-xs text-[#A8A090]">Open a chapter to use poetry tools.</p>;
  if (ctx.mode !== "poem" && ctx.mode !== "song") return (
    <div className="space-y-2 py-4 text-center text-xs text-[#A8A090]" data-testid="poetry-prose-note">
      <Feather className="mx-auto h-5 w-5 text-[#F59E0B]" />
      <p>Switch this chapter to <b className="text-[#F3EBDD]">Poem</b> or <b className="text-[#F3EBDD]">Song</b> in the chapter header to see syllables, rhymes and form guides.</p>
    </div>
  );
  return (
    <div className="space-y-3" data-testid="poetry-widget">
      <div className="flex gap-1">{TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`rounded-full px-2.5 py-0.5 text-xs ${tab === k ? "bg-[#D97706] text-[#0B0D10]" : "text-[#A8A090] hover:bg-[#1A202C]"}`} data-testid={`poetry-tab-${k}`}>{l}</button>)}
        {!dictReady() && <span className="ml-auto text-[10px] text-[#7A7468]" data-testid="poetry-loading">Loading word list…</span>}</div>
      {tab === "rhymes" && <RhymesTab editor={ctx.editor} onInsert={onInsert} />}
      {tab === "guide" && <GuideTab {...ctx} />}
      {tab === "words" && <WordsTab editor={ctx.editor} />}
    </div>
  );
};
